import React from 'react';
import { ProjectScope } from '../utils/projectScope';

interface ProjectScopeTabsProps {
  value: ProjectScope;
  onChange: (value: ProjectScope) => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  onSearchSubmit: () => void;
  onClearSearch: () => void;
  searchLoading?: boolean;
}

const ProjectScopeTabs: React.FC<ProjectScopeTabsProps> = ({
  value,
  onChange,
  searchValue,
  onSearchChange,
  onSearchSubmit,
  onClearSearch,
  searchLoading = false,
}) => {
  const searchActive = value === 'search';
  const showSearchTab = searchValue.trim().length > 0;

  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    const mobileQuery = window.matchMedia('(max-width: 639px)');
    const syncScopeForMobile = (isMobile: boolean) => {
      const isMineScope = value === 'mine' || value === 'mine-nacharbeiten';
      if (isMobile && isMineScope) {
        onChange('all');
      }
    };

    syncScopeForMobile(mobileQuery.matches);
    const handleChange = (event: MediaQueryListEvent) => {
      syncScopeForMobile(event.matches);
    };
    if (typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener('change', handleChange);
      return () => mobileQuery.removeEventListener('change', handleChange);
    }

    mobileQuery.addListener(handleChange);
    return () => mobileQuery.removeListener(handleChange);
  }, [onChange, value]);

  return (
    <div className="border-b border-slate-200/70 bg-white/75 backdrop-blur dark:border-white/10 dark:bg-slate-900/70">
      <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-3 sm:px-6 lg:flex-row lg:items-end lg:justify-between lg:px-8">
        <div className="flex flex-wrap items-end gap-x-8 gap-y-2">
          <button
            type="button"
            onClick={() => onChange('mine')}
            className={`hidden border-b-2 px-1 pb-3 pt-1 text-sm font-semibold transition sm:inline-flex ${
              value === 'mine'
                ? 'border-slate-950 text-slate-950 dark:border-white dark:text-white'
                : 'border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-100'
            }`}
          >
            Meine Projekte
          </button>
          <button
            type="button"
            onClick={() => onChange('all')}
            className={`border-b-2 px-1 pb-3 pt-1 text-sm font-semibold transition ${
              value === 'all'
                ? 'border-slate-950 text-slate-950 dark:border-white dark:text-white'
                : 'border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-100'
            }`}
          >
            Alle Projekte
          </button>
          {showSearchTab && (
            <button
              type="button"
              onClick={() => onChange('search')}
              className={`border-b-2 px-1 pb-3 pt-1 text-sm font-semibold transition ${
                searchActive
                  ? 'border-slate-950 text-slate-950 dark:border-white dark:text-white'
                  : 'border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-100'
              }`}
            >
              Suche
            </button>
          )}
        </div>

        <div className="relative w-full lg:max-w-md">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4 text-slate-400 dark:text-slate-500">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M21 21l-4.35-4.35m1.85-5.15a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>

          <input
            type="text"
            value={searchValue}
            onChange={(event) => onSearchChange(event.target.value)}
            onFocus={() => onChange('search')}
            onKeyDown={(event) => {
              if (event.key === 'Enter') onSearchSubmit();
            }}
            placeholder="Projekte suchen..."
            className={`w-full rounded-full border bg-white py-3 pl-12 pr-12 text-sm text-slate-900 transition focus:bg-white dark:bg-slate-950/80 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:bg-slate-950 ${
              searchActive
                ? 'border-slate-950 shadow-[0_0_0_1px_rgba(15,23,42,0.15)] dark:border-white/30 dark:shadow-[0_0_0_1px_rgba(255,255,255,0.14)]'
                : 'border-slate-200 focus:border-slate-400 dark:border-white/10 dark:focus:border-white/20'
            }`}
          />

          {searchLoading && (
            <div className="pointer-events-none absolute inset-y-0 right-12 flex items-center text-slate-400 dark:text-slate-500">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-slate-200 border-t-slate-500 dark:border-slate-700 dark:border-t-slate-300" />
            </div>
          )}

          {searchValue.trim().length > 0 && (
            <button
              type="button"
              onClick={onClearSearch}
              className="absolute inset-y-0 right-0 flex items-center pr-4 text-slate-400 transition hover:text-slate-700 dark:text-slate-500 dark:hover:text-slate-100"
              aria-label="Suche löschen"
              title="Suche löschen"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectScopeTabs;
